import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { NotificationResponse } from "../Api/Models/user.mode";
import { userService } from "../Api/Services/UserService";
import { useNavigate } from "react-router-dom";

interface NotificationContextProps {
    notifications: NotificationResponse | null;
    fetchNotifications: (lastFetchTime: string | null) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<NotificationResponse | null>(null);
    const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);
    const navigate = useNavigate();

    const fetchNotifications = useCallback(async (lastFetchTime: string | null) => {
        const now = Date.now();

        if (lastFetchTime && now - Number(lastFetchTime) < 60000) {
            console.log("Skipping fetch: Last fetch was less than 1 minute ago.");
            return;
        }

        try {
            const response = await userService.notifications(lastFetchTime);
            setNotifications(response);
            setLastFetchTime(now);

            if (response.isFirstLogin === '1') {
                navigate("/password/change", { state: { isFirstLogin: true } });
            }
            console.log("Notifications:", response);
            console.log("Fetched notifications at:", new Date(now).toISOString());
        } catch (err: unknown) {
            console.error("Error fetching notifications:", err);

            const error = err as { message?: string };

            if (error?.message === "Unauthenticated.") {
                localStorage.removeItem("access_token");
                sessionStorage.removeItem("access_token");
                window.history.replaceState(null, "", "/");
                window.location.replace(window.location.pathname + "?t=" + new Date().getTime());
            }
        }
    }, [navigate]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchNotifications(lastFetchTime ? lastFetchTime.toString() : null);
        }, 60000);

        return () => clearInterval(interval);
    }, [lastFetchTime, fetchNotifications]);

    return (
        <NotificationContext.Provider value={{ notifications, fetchNotifications }}>
            {children}
        </NotificationContext.Provider>
    );
};


export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error(
            "useNotifications must be used within a NotificationProvider"
        );
    }
    return context;
};
